// One-time permission priming run after a successful login so the browser
// has already asked for camera/geolocation before the user taps a big red
// Clock-In button. Both calls are best-effort — a deny here does not block
// login.

export async function primeCameraPermission(): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user" },
      audio: false,
    });
    // Immediately release: we only wanted the permission prompt.
    stream.getTracks().forEach((t) => t.stop());
  } catch {
    // Deny / not-now / error — handled at clock time.
  }
}

export function primeGeolocationPermission(): void {
  if (typeof navigator === "undefined" || !navigator.geolocation) return;
  try {
    navigator.geolocation.getCurrentPosition(
      () => {},
      () => {},
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60_000 },
    );
  } catch {
    // no-op
  }
}

export interface GeolocationSample {
  latitude: number | null;
  longitude: number | null;
  gps_accuracy_m: number | null;
  failed: boolean;
}

export async function captureGeolocation(): Promise<GeolocationSample> {
  if (!navigator.geolocation) {
    return {
      latitude: null,
      longitude: null,
      gps_accuracy_m: null,
      failed: true,
    };
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          gps_accuracy_m: pos.coords.accuracy,
          failed: false,
        }),
      () =>
        resolve({
          latitude: null,
          longitude: null,
          gps_accuracy_m: null,
          failed: true,
        }),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  });
}
